import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]     = useState(null);
  const [agents, setAgents] = useState([]);   // agent_keys assigned to this user
  const [loading, setLoading] = useState(true);

  // ── Restore session on mount ───────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("treasury_token");
      if (!token) { setLoading(false); return; }
      try {
        const { data } = await api.get("/auth/me");
        // /auth/me returns { user, agents } OR just the user object
        const u = data.user ?? data;
        const a = data.agents ?? [];
        setUser(u);
        setAgents(a.map((ag) => (typeof ag === "string" ? ag : ag.agent_key)));
      } catch {
        localStorage.removeItem("treasury_token");
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Login ──────────────────────────────────────────────────────────────────
  // Returns the user object so the caller can check is_first_login.
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });

    // Backend returns: { access_token, token_type, user, agents }
    // api.js interceptor uses "treasury_token" as the key
    const token = data.access_token || data.token;
    if (token) localStorage.setItem("treasury_token", token);

    const u = data.user ?? data;
    const a = data.agents ?? [];
    setUser(u);
    setAgents(a.map((ag) => (typeof ag === "string" ? ag : ag.agent_key)));

    return u; // caller checks u.is_first_login
  };

  // ── Change password (first-login flow) ────────────────────────────────────
  const changePassword = async (currentPassword, newPassword) => {
    await api.post("/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
    // Flip the flag locally so guards re-evaluate immediately
    setUser((prev) => ({ ...prev, is_first_login: false }));
  };

  // ── Logout ─────────────────────────────────────────────────────────────────
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    localStorage.removeItem("treasury_token");
    setUser(null);
    setAgents([]);
  };

  // ── Helper: does this user have access to an agent key? ───────────────────
  // Admins and product_admins bypass the check entirely.
  const hasAgent = (key) => {
    if (!user) return false;
    if (user.is_admin || user.role === "admin" || user.role === "product_admin") return true;
    return agents.includes(key);
  };

  return (
    <AuthContext.Provider value={{
      user, agents, loading,
      login, changePassword, logout, hasAgent,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);